<script setup lang="ts">
  import { useDebounceFn } from '@vueuse/core';
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { DEBOUNCE_TIME_MS } from '@stuut/common/src/constants';
  import { Input } from '@ui/components/input';
  import { cn, isEmailValid } from '@ui/utils/utils';

  const props = defineProps<{
    defaultValue?: string;
    modelValue?: string | null;
    placeholder?: string;
    class?: HTMLAttributes['class'];
    handleKeyUp?: (e: Event) => void;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', payload: string | null): void;
    (
      e: 'change',
      payload: {
        email: string | null;
        isValidEmail: boolean;
      },
    ): void;
  }>();

  const emailInput = ref('');
  const originalEmail = ref<string | null>(null);

  // Compute if email is valid
  const isValidEmail = computed(() => {
    if (!emailInput.value) return true; // Empty is considered valid (optional field)
    return isEmailValid(emailInput.value);
  });

  // Check if email has changed from original
  const isEmailChanged = computed(() => {
    return emailInput.value !== (originalEmail.value || '');
  });

  // Watch for changes to emit them
  watch(emailInput, (newEmail) => {
    const emailValue = newEmail || null;
    emit('update:modelValue', emailValue);
  });

  // Watch modelValue prop to sync with parent
  watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue !== emailInput.value) {
        emailInput.value = newValue || '';
      }
    },
  );

  const handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    emailInput.value = input.value;
  };

  const handleKeyUp = useDebounceFn((e: Event) => {
    props.handleKeyUp?.(e);
  }, DEBOUNCE_TIME_MS);

  // Initialize from props
  onBeforeMount(() => {
    const initialValue = props.modelValue || props.defaultValue || '';
    emailInput.value = initialValue;
    originalEmail.value = initialValue || null;
  });
</script>

<template>
  <div
    :class="
      cn(
        'email-input rounded',
        props.class,
        emailInput && isEmailChanged
          ? isValidEmail
            ? 'border-success'
            : 'border-destructive'
          : '',
      )
    "
  >
    <Input
      v-model="emailInput"
      type="email"
      :placeholder="placeholder || 'Enter email address'"
      class="w-full"
      @input="handleInput"
      @keyup="handleKeyUp"
    />
  </div>
</template>

<style lang="postcss" scoped></style>
